import { useWindowScroll } from 'react-use';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setHasClosedAdhesionAd } from 'redux-implementations/session';
import closeButton from 'public/static/images/adhesion-close-button.svg';
import { X_SMALL } from 'lib/DeviceScreen';
import { TINY_RECTANGLE } from 'lib/GoogleAds/Sizes';
import State from 'types/State';
import TimeIntervalRefreshAd from './TimeIntervalRefreshAd';

// the amount of time after the ad is visible before the close button is shown
export const CLOSE_BUTTON_DELAY = 5000;

// the amount of time the ad is show before it refreshes to show a new ad
export const AD_REFRESH_TIME = 30000;

// the maximum number of times the ad can be refreshed, note that an add must be
// loaded once before it can be refreshed, so a given ad slot can show a maximum
// of AD_REFRESH_LIMIT + 1 separate ads.
export const AD_REFRESH_LIMIT = 9;

function useIsInView(visibilityBreakpoint: number): boolean {
  const { y } = useWindowScroll();

  return y >= visibilityBreakpoint;
}

interface Props {
  visibilityBreakpoint: number;
  adhereToBottom?: boolean;
  targeting: number;
}

export default function TimeIntervalRefreshAdhesionAd({
  visibilityBreakpoint,
  adhereToBottom = false,
  targeting,
}: Props): JSX.Element | null {
  const hasClosedAdhesionAd = useSelector<State>((state) => state.session.hasClosedAdhesionAd);
  const dispatch = useDispatch();
  const isInView = useIsInView(visibilityBreakpoint);
  const [isHidden, setIsHidden] = useState(false);
  const [isCloseButtonHidden, setIsCloseButtonHidden] = useState(true);

  function onClickCloseButton(): void {
    setIsHidden(true);
    dispatch(setHasClosedAdhesionAd());
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isHidden && isInView) {
      const closeButtonVisibilityInterval = setTimeout(() => {
        setIsCloseButtonHidden(false);
      }, CLOSE_BUTTON_DELAY);

      return (): void => clearTimeout(closeButtonVisibilityInterval);
    }
  }, [isHidden, isInView]);

  if (!isInView || isHidden || hasClosedAdhesionAd) {
    return null;
  }

  return (
    <div className="container">
      <TimeIntervalRefreshAd
        adConfig={{
          dimensions: [[320, 50]],
          targeting: { inLine: targeting },
          screenSizes: [X_SMALL],
        }}
        placeholderSize={TINY_RECTANGLE}
        refreshTime={AD_REFRESH_TIME}
        refreshLimit={AD_REFRESH_LIMIT}
      />
      {isCloseButtonHidden ? null : (
        <button aria-label="close" onClick={onClickCloseButton} className="close-button" />
      )}
      <style jsx>{`
        .container {
          position: fixed;
          z-index: 100;
          top: ${adhereToBottom ? undefined : '0'};
          bottom: ${adhereToBottom ? '0' : undefined};
          display: flex;
          justify-content: center;
          background-color: white;
          width: 100%;
        }

        .close-button {
          position: absolute;
          top: ${adhereToBottom ? '-11px' : undefined};
          bottom: ${adhereToBottom ? undefined : '-11px'};
          right: 2px;
          background-color: transparent;
          background-image: url(${closeButton});
          background-size: 24px 24px;
          background-repeat: no-repeat;
          height: 24px;
          width: 24px;
          border: 0;
        }
      `}</style>
    </div>
  );
}
